// ClockAdapter — Default implementation for high-resolution timing
// Injected into VNInvestCharts to avoid direct window.performance/window.requestAnimationFrame
// All global access is lazy-initialized inside the factory function for tree-shaking
export const createDefaultClockAdapter = () => {
    // Use performance.now() for high-resolution timestamps
    const now = () => {
        if (typeof performance !== 'undefined' && performance.now) {
            return performance.now();
        }
        // Fallback for environments without performance.now()
        return Date.now();
    };
    // requestAnimationFrame wrapper
    const requestFrame = (callback) => {
        if (typeof requestAnimationFrame !== 'undefined') {
            return requestAnimationFrame((time) => callback(time));
        }
        // Fallback: setTimeout ~60fps
        if (typeof window !== 'undefined' && window.setTimeout) {
            return Number(window.setTimeout(() => callback(now()), 1000 / 60));
        }
        // Last resort fallback
        return Number(setTimeout(() => callback(now()), 1000 / 60));
    };
    const cancelFrame = (id) => {
        if (typeof cancelAnimationFrame !== 'undefined') {
            cancelAnimationFrame(id);
        }
        else if (typeof window !== 'undefined' && window.clearTimeout) {
            window.clearTimeout(id);
        }
        else {
            clearTimeout(id);
        }
    };
    // setTimeout wrapper
    const setTimeout = (callback, delay) => {
        if (typeof window !== 'undefined' && window.setTimeout) {
            return Number(window.setTimeout(callback, delay));
        }
        return Number(setTimeout(callback, delay));
    };
    // clearTimeout wrapper
    const clearTimeout = (id) => {
        if (typeof window !== 'undefined' && window.clearTimeout) {
            window.clearTimeout(id);
        }
        else {
            clearTimeout(id);
        }
    };
    return { now, requestFrame, cancelFrame, setTimeout, clearTimeout };
};
// Singleton - lazy initialized on first access
let _defaultClockAdapter = null;
export const getDefaultClockAdapter = () => {
    if (!_defaultClockAdapter) {
        _defaultClockAdapter = createDefaultClockAdapter();
    }
    return _defaultClockAdapter;
};
// Backward compatibility - lazy getter
export const defaultClockAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultClockAdapter();
        return adapter[prop];
    }
});
export const createVirtualClockAdapter = (initialTime = 0) => {
    let currentTime = initialTime;
    let frameIdCounter = 0;
    const pendingFrames = new Map();
    return {
        now: () => currentTime,
        requestFrame: (callback) => {
            const id = ++frameIdCounter;
            pendingFrames.set(id, callback);
            return id;
        },
        cancelFrame: (id) => {
            pendingFrames.delete(id);
        },
        setTimeout: (callback, delay) => {
            const id = ++frameIdCounter;
            // In virtual clock, we don't actually delay, just store for manual execution
            pendingFrames.set(id, () => callback());
            return id;
        },
        clearTimeout: (id) => {
            pendingFrames.delete(id);
        },
        // Test controls
        advanceTime: (ms) => {
            currentTime += ms;
        },
        runPendingFrames: () => {
            const frames = Array.from(pendingFrames.entries());
            pendingFrames.clear();
            frames.forEach(([id, callback]) => {
                callback(currentTime);
            });
        },
        getCurrentTime: () => currentTime,
    };
};
export const createManualClockAdapter = (initialTime = 0) => {
    let currentTime = initialTime;
    let frameIdCounter = 0;
    const pendingFrames = new Map();
    return {
        now: () => currentTime,
        requestFrame: (callback) => {
            const id = ++frameIdCounter;
            pendingFrames.set(id, callback);
            return id;
        },
        cancelFrame: (id) => {
            pendingFrames.delete(id);
        },
        setTimeout: (callback, delay) => {
            const id = ++frameIdCounter;
            pendingFrames.set(id, () => callback());
            return id;
        },
        clearTimeout: (id) => {
            pendingFrames.delete(id);
        },
        // Manual controls
        tick: (ms = 16.67) => {
            currentTime += ms;
        },
        runFrame: () => {
            const frames = Array.from(pendingFrames.entries());
            pendingFrames.clear();
            frames.forEach(([id, callback]) => {
                callback(currentTime);
            });
        },
    };
};
